import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Divider,
  Flex,
  IconButton,
  Img,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useApp } from '@hb/react'
import {
  AppName,
  Assessment,
  colors,
  getAssessmentName,
  getV2PregnancyTabs,
  isSubTabGroup,
  PracticeVisit,
} from '@hb/shared'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useProfile } from '../../../../../../contexts/user/ProfileContext'
import { useProfileLayout } from '../../../../../../contexts/user/ProfileLayoutContext'
import { ProfileEntity } from '../../../../../../contexts/user/types'
import pregnancyIcon from '../../../../../../icons/breastfeeding.svg'
import healthCrossIcon from '../../../../../../icons/health_cross_red.svg'
import patientIcon from '../../../../../../icons/person_fill.svg'

interface PathPart {
  header?: string
  name: string
  path: string | null
  entity?: ProfileEntity
  icon?: {
    src: string
    alt: string
    filter?: string
    invertFilter?: string
  }
  children?: PathPart[]
}

const Separator = () => (
  <ChevronRightIcon w={6} h={6} color="white" filter="drop-shadow(1px 1px 3px #00000077)" />
)

const LeafPathButton = ({
  part,
  depth = 0,
  asMenuTrigger = false,
  onNavigate,
}: {
  part: PathPart
  depth?: number
  asMenuTrigger?: boolean
  onNavigate?: () => void
}) => {
  const { icon, name, header, entity } = part
  const { getTabPath } = useProfile()
  const { layoutMode } = useProfileLayout()
  const isMobile = layoutMode === 'mobile'
  const path = useMemo(
    () => getTabPath({ entity: entity ?? { type: 'patient' }, tab: part.path }),
    [entity, getTabPath, part.path],
  )
  const button = (
    <Button
      variant="link"
      colorScheme="whiteAlpha"
      // onClick={() => onTabSelect({ entity: entity ?? { type: 'patient' }, tab: part.path })}
      fontFamily="Hero-New"
      fontWeight={500}
      w="100%"
      justifyContent="flex-start"
      textShadow={depth ? 'none' : '1px 1px 3px #00000077'}
      color={depth ? 'gray.600' : 'white'}>
      <Flex gap={1} align="center">
        {icon ? (
          <Img
            src={icon.src}
            alt={icon.alt}
            h={isMobile ? '18px' : '24px'}
            filter={depth ? icon.invertFilter : icon.filter}
          />
        ) : null}
        <Flex align="flex-start" flexFlow="column">
          {header ? (
            <Text whiteSpace="nowrap" lineHeight={1.2} fontSize={isMobile ? '10px' : 'xs'}>
              {header.toUpperCase()}
            </Text>
          ) : null}
          <Text
            whiteSpace="nowrap"
            fontSize={isMobile ? 'sm' : undefined}
            maxW={layoutMode === 'desktop' ? '300px' : '180px'}
            isTruncated
            lineHeight={1}>
            {name}
          </Text>
        </Flex>
      </Flex>
    </Button>
  )
  // when used as a click-popover trigger, the button only opens the menu - no link
  if (asMenuTrigger) return button
  return (
    <Link style={{ opacity: 1 }} to={path} onClick={onNavigate}>
      {button}
    </Link>
  )
}

const GroupPathButton = ({
  part,
  depth = 0,
  onNavigate,
}: {
  part: PathPart
  depth?: number
  onNavigate?: () => void
}) => {
  const { children, path } = part
  const { layoutMode } = useProfileLayout()
  // hover popovers are unusable on touch - open on click below desktop width
  const compact = layoutMode !== 'desktop'
  const menuChildren = useMemo(
    // the click trigger swallows the group's own link, so keep its page reachable from the menu
    () => (compact ? [{ ...part, children: undefined }, ...(children ?? [])] : children),
    [compact, part, children],
  )
  return (
    <Box w={depth ? '100%' : 'auto'} position="relative">
      <Popover
        isLazy
        placement={depth % 2 === 0 ? 'bottom-start' : 'right-start'}
        strategy="fixed"
        trigger={compact ? 'click' : 'hover'}>
        {({ onClose }) => {
          const handleNavigate = () => {
            onClose()
            onNavigate?.()
          }
          return (
            <>
              <PopoverTrigger>
                <Box>
                  <LeafPathButton asMenuTrigger={compact} depth={depth} part={part} />
                </Box>
              </PopoverTrigger>
              <PopoverContent w="auto">
                <PopoverBody maxH="360px" overflowY="auto" p={0}>
                  <VStack spacing={0} divider={<Divider />} align="flex-start" flexFlow="column">
                    {menuChildren?.map((child, i) => (
                      <Box key={`${path}-child-${i}`} w="100%" pb={1} pt={2} px={2}>
                        <PathButton
                          depth={depth + 1}
                          key={i}
                          part={child}
                          onNavigate={handleNavigate}
                        />
                      </Box>
                    ))}
                  </VStack>
                </PopoverBody>
                {depth ? <PopoverArrow /> : null}
              </PopoverContent>
            </>
          )
        }}
      </Popover>
    </Box>
  )
}

const PathButton = ({
  part,
  depth = 0,
  onNavigate,
}: {
  part: PathPart
  depth?: number
  onNavigate?: () => void
}) =>
  part.children ? (
    <GroupPathButton depth={depth} part={part} onNavigate={onNavigate} />
  ) : (
    <LeafPathButton depth={depth} part={part} onNavigate={onNavigate} />
  )

const getAssessmentPathPart = (
  appName: AppName,
  assessmentId: string,
  assessment: Assessment | null,
): PathPart => ({
  name: getAssessmentName(assessment),
  header: 'Pregnancy',
  path: '',
  entity: { type: 'pregnancy', id: assessmentId },
  children: getV2PregnancyTabs(appName, assessmentId).filter(t => t.path !== 'patient'),
  icon: {
    src: pregnancyIcon,
    alt: 'Pregnancy',
    filter: 'brightness(150%) drop-shadow(1px 1px 3px #00000077)',
    invertFilter: 'brightness(50%)',
  },
})

const getPatientPathPart = (
  appName: AppName,
  reverseName: string,
  assessments: Record<string, Assessment> | null,
  officeVisits: Record<string, PracticeVisit> | null,
): PathPart => ({
  name: reverseName,
  header: 'Patient',
  entity: { type: 'patient' },
  path: null,
  icon: {
    src: patientIcon,
    alt: 'Patient',
    filter: 'brightness(300%) drop-shadow(1px 1px 3px #00000077)',
    invertFilter: 'brightness(50%)',
  },
  children: [
    ...Object.entries(assessments ?? {}).map(([id, a]) => getAssessmentPathPart(appName, id, a)),
    // {
    //   name: `Office Visits (${Object.keys(officeVisits ?? {}).length})`,
    //   path: 'visits',
    // },
    {
      name: 'Gyn Care',
      path: 'gyn',
      icon: {
        src: healthCrossIcon,
        alt: 'Health Cross',
      },
    },
  ],
})

export const BodyHeader = () => {
  const {
    user,
    subTab,
    tab,
    selectedAssessment,
    assessmentId,
    assessments,
    getTabPath,
    officeVisits: { data: officeVisits },
  } = useProfile()
  const { layoutMode } = useProfileLayout()
  const { reverseName } = user ?? {}

  const { appName } = useApp()
  const pathParts = useMemo(() => {
    const parts: PathPart[] = [
      getPatientPathPart(appName, reverseName || 'No name entered', assessments, officeVisits),
    ]
    const tabName = tab?.path ?? ''
    if (tabName === 'patient' && !subTab) return parts

    switch (tabName) {
      case 'patient':
        break
      default:
        if (assessmentId)
          parts.push(
            getAssessmentPathPart(appName, assessmentId, selectedAssessment.assessmentData),
          )
        break
    }
    if (!tab) return parts
    // parts.push({ name: tab.name, path: tab.path })
    if (isSubTabGroup(tab) && subTab) {
      const subTabGroup = tab.children?.find(t => t.path === subTab.path)
      if (subTabGroup) {
        parts.push({ name: subTabGroup.name, path: subTabGroup.path })
      }
    }
    return parts
  }, [
    subTab,
    tab,
    selectedAssessment,
    reverseName,
    assessmentId,
    assessments,
    officeVisits,
    appName,
  ])

  // on mobile, collapse the breadcrumb to a back chevron + the current entity
  const parentPart =
    layoutMode === 'mobile' && pathParts.length > 1 ? pathParts[pathParts.length - 2] : null
  const displayedParts = parentPart ? pathParts.slice(-1) : pathParts

  const isMobile = layoutMode === 'mobile'

  return (
    <Flex
      align="center"
      bg={colors.pink.hex}
      w="100%"
      px={isMobile ? 1 : 2}
      py={isMobile ? 1 : 2}
      minW={0}
      overflow="hidden">
      {parentPart ? (
        <Link
          to={getTabPath({
            entity: parentPart.entity ?? { type: 'patient' },
            tab: parentPart.path,
          })}>
          <IconButton
            aria-label={`Back to ${parentPart.name}`}
            size="xs"
            variant="ghost"
            _hover={{ bg: 'whiteAlpha.300' }}
            icon={
              <ChevronLeftIcon
                w={5}
                h={5}
                color="white"
                filter="drop-shadow(1px 1px 3px #00000077)"
              />
            }
          />
        </Link>
      ) : null}
      {displayedParts.map((part, i) => (
        <Flex key={`${part.entity ? part.entity.type : ''}-${part.path}`} alignItems="center">
          {i ? <Separator /> : null}
          <PathButton part={part} />
        </Flex>
      ))}
    </Flex>
  )
}
